import { useMemo, useRef, useState, type KeyboardEvent } from 'react'

import type { PublicUser } from '@/types/api'

import { Avatar } from './components/IssueBadges'
import { cn } from './lib/ui'
import type { MemberRow } from './types'

/**
 * A comment box with @mention autocomplete.
 *
 * The inserted form is `@[Full Name]` — the delimited variant the server's
 * `ParseMentions` understands unambiguously. It also accepts `@email`,
 * `@local-part` and a bare `@name`, but those are dropped when two members
 * share a handle rather than notifying the wrong person, so the delimited form
 * is what the UI always writes.
 *
 * Candidates come from the workspace roster, which is exactly the set the
 * server resolves against: a mention can never name someone outside the tenant.
 */
export function MentionTextarea({
  value,
  onChange,
  members,
  placeholder,
  rows = 3,
  disabled,
  onSubmit,
  className,
  id,
}: {
  value: string
  onChange: (next: string) => void
  members: MemberRow[]
  placeholder?: string
  rows?: number
  disabled?: boolean
  onSubmit?: () => void
  className?: string
  id?: string
}) {
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const [query, setQuery] = useState<string | null>(null)
  const [highlight, setHighlight] = useState(0)

  const candidates = useMemo(() => {
    if (query === null) return []
    const term = query.toLowerCase()
    return members
      .filter((member) => {
        if (!member.user) return false
        if (!term) return true
        return (
          member.user.name.toLowerCase().includes(term) ||
          member.user.email.toLowerCase().includes(term)
        )
      })
      .slice(0, 6)
  }, [members, query])

  const updateQuery = (text: string, caret: number) => {
    const before = text.slice(0, caret)
    // An @ that starts a word, followed by what has been typed since.
    const match = /(?:^|\s)@([\w.\-']*)$/.exec(before)
    setQuery(match ? (match[1] ?? '') : null)
    setHighlight(0)
  }

  const insert = (user: PublicUser) => {
    const textarea = textareaRef.current
    const caret = textarea?.selectionStart ?? value.length
    const before = value.slice(0, caret)
    const after = value.slice(caret)
    const replaced = before.replace(/@([\w.\-']*)$/, `@[${user.name}] `)
    const next = replaced + after
    onChange(next)
    setQuery(null)
    requestAnimationFrame(() => {
      const position = replaced.length
      textarea?.focus()
      textarea?.setSelectionRange(position, position)
    })
  }

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (query !== null && candidates.length > 0) {
      if (event.key === 'ArrowDown') {
        event.preventDefault()
        setHighlight((index) => (index + 1) % candidates.length)
        return
      }
      if (event.key === 'ArrowUp') {
        event.preventDefault()
        setHighlight((index) => (index - 1 + candidates.length) % candidates.length)
        return
      }
      if (event.key === 'Enter' || event.key === 'Tab') {
        const picked = candidates[highlight]?.user
        if (picked) {
          event.preventDefault()
          insert(picked)
          return
        }
      }
      if (event.key === 'Escape') {
        // Dismissing the mention menu must not also close the issue panel.
        event.preventDefault()
        event.stopPropagation()
        setQuery(null)
        return
      }
    }

    if (onSubmit && event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
      event.preventDefault()
      onSubmit()
    }
  }

  return (
    <div className="relative">
      <textarea
        id={id}
        ref={textareaRef}
        value={value}
        rows={rows}
        disabled={disabled}
        placeholder={placeholder}
        onChange={(event) => {
          onChange(event.target.value)
          updateQuery(event.target.value, event.target.selectionStart ?? 0)
        }}
        onKeyDown={onKeyDown}
        onBlur={() => window.setTimeout(() => setQuery(null), 120)}
        className={cn(
          'w-full resize-y rounded-md border border-ink-300 bg-white px-2.5 py-2 text-sm text-ink-900 shadow-sm transition',
          'placeholder:text-ink-400 focus:border-brand-500',
          'dark:border-ink-700 dark:bg-ink-950 dark:text-ink-100',
          className,
        )}
      />

      {query !== null && candidates.length > 0 && (
        <ul
          role="listbox"
          aria-label="Mention a teammate"
          className="absolute bottom-full left-2 z-30 mb-1 w-64 overflow-hidden rounded-card border border-ink-200 bg-white p-1 shadow-lg dark:border-ink-800 dark:bg-ink-900"
        >
          {candidates.map((member, index) => (
            <li key={member.user_id}>
              <button
                type="button"
                role="option"
                aria-selected={index === highlight}
                onMouseDown={(event) => {
                  event.preventDefault()
                  if (member.user) insert(member.user)
                }}
                onMouseEnter={() => setHighlight(index)}
                className={cn(
                  'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-sm',
                  index === highlight
                    ? 'bg-brand-50 text-brand-800 dark:bg-brand-950 dark:text-brand-200'
                    : 'text-ink-700 dark:text-ink-200',
                )}
              >
                <Avatar user={member.user} size="xs" />
                <span className="min-w-0 flex-1 truncate">{member.user?.name}</span>
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}

/**
 * Renders a comment body with its mentions picked out.
 *
 * Deliberately not markdown and deliberately not `dangerouslySetInnerHTML`:
 * comment text is user input that arrives from anyone in the workspace, and an
 * MCP agent can write it too.
 */
export function CommentBody({ body, mentioned }: { body: string; mentioned: PublicUser[] }) {
  const names = useMemo(
    () => new Set(mentioned.map((user) => user.name.toLowerCase())),
    [mentioned],
  )

  const parts = useMemo(() => {
    const pattern = /@\[([^\]]+)\]|@"([^"]+)"|@([\w.\-'@]+)/g
    const output: Array<{ text: string; mention: boolean }> = []
    let last = 0
    let match: RegExpExecArray | null
    while ((match = pattern.exec(body)) !== null) {
      if (match.index > last) output.push({ text: body.slice(last, match.index), mention: false })
      const name = match[1] ?? match[2] ?? match[3] ?? ''
      output.push({ text: `@${name}`, mention: names.has(name.toLowerCase()) || names.size === 0 })
      last = match.index + match[0].length
    }
    if (last < body.length) output.push({ text: body.slice(last), mention: false })
    return output
  }, [body, names])

  return (
    <p className="whitespace-pre-wrap break-words text-sm text-ink-700 dark:text-ink-200">
      {parts.map((part, index) =>
        part.mention ? (
          <span
            key={index}
            className="rounded bg-brand-50 px-1 font-medium text-brand-700 dark:bg-brand-950 dark:text-brand-300"
          >
            {part.text}
          </span>
        ) : (
          <span key={index}>{part.text}</span>
        ),
      )}
    </p>
  )
}
